import * as React from "react";
import { AlertCircle, Loader2 } from "lucide-react";
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Textarea,
  cn,
  toast,
} from "@eduviaos/ui-kit";
import { ApiError } from "@eduviaos/shared";
import { useReleaseSeatAssignment, type SeatAssignment } from "@/lib/seating/allocations";

/**
 * Phase F-finale-7 Slice 7b.4c — Release-assignment confirm dialog.
 *
 * Releasing a permanent seat is destructive-ish (it frees the seat + clears
 * the member's allocation), so it's a modal confirm — not a one-click row
 * action — and the BE requires a non-empty `reason` (1..2000) that is stored
 * as `release_reason` for the audit trail. The dialog blocks submit until a
 * reason is entered and surfaces BE errors inline without closing.
 */

export interface ReleaseAssignmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assignment: SeatAssignment | null;
  /** Resolved labels for the confirm copy (fall back to ids). */
  seatLabel?: string;
  studentLabel?: string;
  onReleased?: (released: SeatAssignment) => void;
}

export function ReleaseAssignmentDialog({
  open,
  onOpenChange,
  assignment,
  seatLabel,
  studentLabel,
  onReleased,
}: ReleaseAssignmentDialogProps) {
  const [reason, setReason] = React.useState("");
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setReason("");
      setSubmitError(null);
    }
  }, [open]);

  const releaseMutation = useReleaseSeatAssignment();
  const isSubmitting = releaseMutation.isPending;
  const trimmedReason = reason.trim();
  const canSubmit = trimmedReason.length > 0 && trimmedReason.length <= 2000 && !isSubmitting;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    if (!assignment || !canSubmit) return;

    try {
      const released = await releaseMutation.mutateAsync({
        id: assignment.id,
        body: { reason: trimmedReason },
      });
      onReleased?.(released);
      toast.success(`Released ${seatLabel ?? "seat"}.`);
      onOpenChange(false);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message || "Couldn't release the seat. Please retry."
          : err instanceof Error
            ? err.message
            : "Couldn't release the seat. Please retry.";
      setSubmitError(message);
    }
  };

  const seatText = seatLabel ?? assignment?.seat_id ?? "this seat";
  const studentText = studentLabel ?? assignment?.student_id ?? "the member";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]" data-testid="release-assignment-dialog">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Release seat</DialogTitle>
            <DialogDescription>
              This frees <span className="text-foreground font-medium">{seatText}</span> and clears{" "}
              <span className="text-foreground font-medium">{studentText}</span>&apos;s allocation.
              The seat becomes available to assign to someone else.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-4">
            {submitError ? (
              <div
                role="alert"
                className="border-destructive/30 bg-destructive/5 text-destructive flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
              >
                <AlertCircle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
                <span className="flex-1">{submitError}</span>
              </div>
            ) : null}

            <div>
              <Label htmlFor="release-reason" className="text-xs">
                Reason<span className="text-destructive ml-0.5">*</span>
              </Label>
              <Textarea
                id="release-reason"
                value={reason}
                maxLength={2000}
                required
                autoFocus
                rows={3}
                className="mt-1"
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Member graduated / moved branch / seat reallocated"
              />
              <p className="text-muted-foreground mt-1 text-xs">
                Recorded on the assignment&apos;s audit trail.
              </p>
            </div>
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" type="button" disabled={isSubmitting}>
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" variant="destructive" disabled={!canSubmit}>
              {isSubmitting ? (
                <Loader2 aria-hidden="true" className={cn("size-4 animate-spin")} />
              ) : null}
              Release seat
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
